import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleUp, faArrowCircleDown, faChevronRight, faChevronDown, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import './dropdown-fix.css';
import './enhanced-dropdown.css';

/**
 * A hierarchical multi-select dropdown component with parent/child relationship support
 * 
 * Features:
 * - Expand/collapse icons for parent items
 * - Automatic selection of all children when parent is selected
 * - Automatic selection of parent when all children are selected
 * - Partial selection state for parents with some children selected
 * - Visual hierarchy with indentation and connecting lines
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the dropdown is open
 * @param {Function} props.toggleDropdown - Function to toggle dropdown
 * @param {string} props.displayText - Text to display in the dropdown header
 * @param {Array} props.options - Array of hierarchical options with children property
 * @param {Function} props.isSelected - Function to check if an option is selected (option) => boolean
 * @param {Function} props.onSelect - Function called when an option is selected (option) => void
 * @param {Object} props.themeStyles - Object containing theme-specific styles
 * @param {boolean} props.isDarkMode - Whether the application is in dark mode
 * @param {string} props.label - ARIA label for the dropdown
 * @param {number} props.uiScale - Scale factor for UI sizing (default 1)
 */
const HierarchicalMultiSelectDropdown = ({
  isOpen,
  toggleDropdown,
  displayText,
  options,
  isSelected,
  onSelect,
  themeStyles,
  isDarkMode,
  label = "Select options",
  uiScale = 1
}) => {
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const headerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownWidth, setDropdownWidth] = useState('auto');
  const [isInSimulateExam, setIsInSimulateExam] = useState(false);
  const [isInPracticeTest, setIsInPracticeTest] = useState(false);
  
  // State to track expanded parent items
  const [expandedItems, setExpandedItems] = useState({});

  // UI scaling calculations
  const s = uiScale ?? 1;
  const baseFontSize = Math.round(14 * s);
  const basePaddingY = Math.round(10 * s);
  const basePaddingX = Math.round(12 * s);
  const baseIconSize = Math.round(14 * s);
  const baseBorderRadius = Math.round(5 * s);
  const baseItemHeight = Math.round(46 * s);
  const baseItemPaddingY = Math.round(12 * s);
  const baseItemPaddingX = Math.round(15 * s);
  const baseMinHeight = Math.round(22 * s);
  
  // Search functionality state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);

  // Initialize expanded state for parent items
  useEffect(() => {
    if (isOpen && Array.isArray(options)) {
      setExpandedItems(prevExpandedItems => {
        const expandedState = {...prevExpandedItems};
        
        const findParentItems = (items) => {
          if (!Array.isArray(items)) return;
          
          items.forEach(item => {
            const hasChildren = typeof item === 'object' && item !== null && 
                              'children' in item && Array.isArray(item.children) && 
                              item.children.length > 0;
            if (hasChildren) {
              const key = typeof item === 'object' && item !== null && 'id' in item ? item.id : 
                         typeof item === 'object' && item !== null && 'name' in item ? item.name : item;
              if (expandedState[key] === undefined) {
                expandedState[key] = false; // Default to collapsed
              }
              findParentItems(item.children);
            }
          });
        };
        
        findParentItems(options);
        
        return Object.keys(expandedState).length !== Object.keys(prevExpandedItems).length 
          ? expandedState 
          : prevExpandedItems;
      });
    }
  }, [isOpen, options]);

  // Search filtering logic
  const filterOptions = (options, searchTerm) => {
    if (!searchTerm.trim()) return options;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    const filterRecursive = (items) => {
      if (!Array.isArray(items)) return [];
      
      return items.reduce((filtered, item) => {
        const itemName = typeof item === 'object' && item !== null ? (item.name || item.label || '') : item.toString();
        const itemNameLower = itemName.toLowerCase();
        const hasChildren = typeof item === 'object' && item !== null && 'children' in item && Array.isArray(item.children) && item.children.length > 0;
        
        // Check if current item matches search
        const itemMatches = itemNameLower.includes(searchLower);
        
        let filteredChildren = [];
        if (hasChildren) {
          filteredChildren = filterRecursive(item.children);
        }
        
        // Include item if:
        // 1. Item name matches search term, OR
        // 2. Any of its children match (and include all matching children)
        if (itemMatches || filteredChildren.length > 0) {
          const filteredItem = hasChildren ? {
            ...item,
            children: filteredChildren.length > 0 ? filteredChildren : item.children
          } : item;
          
          filtered.push(filteredItem);
        }
        
        return filtered;
      }, []);
    };
    
    return filterRecursive(options);
  };

  // Update filtered options when search term or options change
  useEffect(() => {
    const filtered = filterOptions(options, searchTerm);
    setFilteredOptions(filtered);
    
    // Auto-expand parents when searching to show filtered children
    if (searchTerm.trim()) {
      setExpandedItems(prev => {
        const expanded = { ...prev };
        
        const expandParents = (items) => {
          items.forEach(item => {
            const hasChildren = typeof item === 'object' && item !== null && 'children' in item && Array.isArray(item.children) && item.children.length > 0;
            if (hasChildren) {
              const itemKey = typeof item === 'object' && item !== null && 'id' in item ? item.id : 
                             typeof item === 'object' && item !== null && 'name' in item ? item.name : item;
              expanded[itemKey] = true;
              expandParents(item.children);
            }
          });
        };
        
        expandParents(filtered);
        return expanded;
      });
    }
  }, [options, searchTerm]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure the dropdown is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else if (!isOpen) {
      // Clear search when dropdown closes
      setSearchTerm('');
    }
  }, [isOpen]);

  // Helper functions for parent/child selection logic
  const getAllChildIds = (option) => {
    if (!option || !option.children || !Array.isArray(option.children)) return [];
    let childIds = [];
    option.children.forEach(child => {
      const childId = typeof child === 'object' && child !== null ? (child.id || child.name) : child;
      childIds.push(childId);
      childIds = [...childIds, ...getAllChildIds(child)];
    });
    return childIds;
  };

  const getAllChildNames = (option) => {
    if (!option || !option.children || !Array.isArray(option.children)) return [];
    let childNames = [];
    option.children.forEach(child => {
      const childName = typeof child === 'object' && child !== null ? child.name : child;
      childNames.push(childName);
      childNames = [...childNames, ...getAllChildNames(child)];
    });
    return childNames;
  };

  const areAllChildrenSelected = (option) => {
    if (!option || !option.children || !Array.isArray(option.children)) return false;
    return option.children.every(child => {
      const childSelected = isSelected(child);
      const grandChildrenSelected = areAllChildrenSelected(child);
      return childSelected && (grandChildrenSelected || !child.children || child.children.length === 0);
    });
  };

  const areSomeChildrenSelected = (option) => {
    if (!option || !option.children || !Array.isArray(option.children)) return false;
    return option.children.some(child => {
      return isSelected(child) || getSelectionState(child) !== 'unselected';
    });
  };

  const getSelectionState = (option) => {
    const hasChildren = typeof option === 'object' && option !== null && 'children' in option && Array.isArray(option.children) && option.children.length > 0;
    
    if (!hasChildren) {
      return isSelected(option) ? 'selected' : 'unselected';
    }

    const allChildrenSelected = areAllChildrenSelected(option);
    const someChildrenSelected = areSomeChildrenSelected(option);

    if (allChildrenSelected) {
      return 'selected';
    } else if (someChildrenSelected) {
      return 'partial';
    } else {
      return 'unselected';
    }
  };

  // Enhanced option click handler that supports parent/child selection
  const handleOptionSelect = (option, e) => {
    // If user clicked directly on the expansion toggle, don't select the option
    if (e.target.classList.contains('expansion-toggle')) {
      return;
    }

    const hasChildren = typeof option === 'object' && option !== null && 'children' in option && Array.isArray(option.children) && option.children.length > 0;
    
    if (hasChildren) {
      // Parent selection logic
      const allChildrenSelected = areAllChildrenSelected(option);
      
      if (allChildrenSelected) {
        // Deselect parent and all children
        onSelect({ type: 'parent-deselect', option, childIds: getAllChildIds(option), childNames: getAllChildNames(option) });
      } else {
        // Select parent and all children
        onSelect({ type: 'parent-select', option, childIds: getAllChildIds(option), childNames: getAllChildNames(option) });
      }
    } else {
      // Child selection logic - let parent component handle this
      onSelect({ type: 'child-select', option });
    }
  };

  // Calculate the width needed for the longest item
  const calculateOptimalWidth = () => {
    if (!isOpen || !filteredOptions || filteredOptions.length === 0 || !dropdownRef.current) return;

    const tempDiv = document.createElement('div');
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.fontFamily = window.getComputedStyle(dropdownRef.current).fontFamily;

    const checkboxWidth = 35;
    const expandIconWidth = 32;
    
    document.body.appendChild(tempDiv);
    let maxWidth = 0;
    
    const measureOptionText = (opt, depth = 0) => {
      const text = typeof opt === 'object' && opt !== null && 'name' in opt ? opt.name : opt;
      const hasChildren = typeof opt === 'object' && opt !== null && 'children' in opt && Array.isArray(opt.children) && opt.children.length > 0;
      
      tempDiv.style.fontSize = '14px';
      tempDiv.style.fontWeight = hasChildren ? '600' : '400';
      tempDiv.textContent = text;
      
      const textWidth = tempDiv.getBoundingClientRect().width;
      const indentWidth = depth * 20;
      const totalWidth = textWidth + checkboxWidth + expandIconWidth + indentWidth;
      maxWidth = Math.max(maxWidth, totalWidth);
      
      if (hasChildren) {
        opt.children.forEach(child => measureOptionText(child, depth + 1));
      }
    };
    
    filteredOptions.forEach(opt => measureOptionText(opt));
    
    tempDiv.textContent = 'WWW';
    const threeCharWidth = tempDiv.getBoundingClientRect().width;
    document.body.removeChild(tempDiv);
    
    const headerWidth = headerRef.current ? headerRef.current.offsetWidth : 0;
    const optimalWidth = Math.max(headerWidth, maxWidth + threeCharWidth);
    
    setDropdownWidth(optimalWidth);
    
    if (menuRef.current && headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      if (dropdownPosition === 'bottom') {
        menuRef.current.style.top = `${headerRect.bottom + 5}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      } else {
        menuRef.current.style.bottom = `${window.innerHeight - headerRect.top + 5}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      }
    }
  };

  // Position dropdown
  useEffect(() => {
    if (isOpen && dropdownRef.current && menuRef.current && headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      const dropdownItemHeight = baseItemHeight;
      const visibleItemsLimit = 5;
      const maxVisibleHeight = dropdownItemHeight * visibleItemsLimit;
      
      // Count total visible items (including expanded children)
      const countVisibleItems = (items) => {
        let count = 0;
        items.forEach(item => {
          count++;
          const hasChildren = typeof item === 'object' && item !== null && 'children' in item && Array.isArray(item.children) && item.children.length > 0;
          if (hasChildren) {
            const itemKey = typeof item === 'object' && item !== null && 'id' in item ? item.id : 
                           typeof item === 'object' && item !== null && 'name' in item ? item.name : item;
            if (expandedItems[itemKey]) {
              count += countVisibleItems(item.children);
            }
          }
        });
        return count;
      };
      
      const totalVisibleItems = countVisibleItems(filteredOptions);
      // Add extra height for search input (considering padding and border)
      const searchInputHeight = 60;
      const menuHeight = (totalVisibleItems <= visibleItemsLimit ? 
                         totalVisibleItems * dropdownItemHeight : 
                         maxVisibleHeight) + searchInputHeight;
      const spaceBelow = window.innerHeight - headerRect.bottom;
      
      const inSimulateExam = document.querySelector('.simulate-exam') && dropdownRef.current.closest('.simulate-exam');
      const inPracticeTest = document.querySelector('.practice-test') && dropdownRef.current.closest('.practice-test');
      setIsInSimulateExam(inSimulateExam);
      setIsInPracticeTest(inPracticeTest);
      
      if (inSimulateExam || inPracticeTest) {
        setDropdownPosition('bottom');
        menuRef.current.style.position = 'absolute';
        menuRef.current.style.top = `${headerRect.bottom}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      } else if (spaceBelow < menuHeight && headerRect.top > menuHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      
      calculateOptimalWidth();
      
      setTimeout(() => {
        if (menuRef.current) {
          const menuRect = menuRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          
          if (menuRect.right > viewportWidth) {
            const overflowAmount = menuRect.right - viewportWidth;
            const newLeft = Math.max(0, headerRect.left - overflowAmount - 10);
            menuRef.current.style.left = `${newLeft}px`;
          }
        }
      }, 0);
    }
  }, [isOpen, filteredOptions, expandedItems]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        toggleDropdown();
      }
    }
    
    function handleScroll() {
      if (isOpen && menuRef.current && headerRef.current) {
        const headerRect = headerRef.current.getBoundingClientRect();
        
        if (dropdownPosition === 'bottom') {
          menuRef.current.style.top = `${headerRect.bottom + 5}px`;
          menuRef.current.style.left = `${headerRect.left}px`;
        } else {
          menuRef.current.style.bottom = `${window.innerHeight - headerRect.top + 5}px`;
          menuRef.current.style.left = `${headerRect.left}px`;
        }
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, toggleDropdown, dropdownPosition]);

  // Prevent body scrolling on touch devices
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      
      const preventScroll = (e) => {
        if (menuRef.current && menuRef.current.contains(e.target)) {
          return;
        }
        window.scrollTo(0, scrollY);
      };
      
      if ('ontouchstart' in window) {
        window.addEventListener('scroll', preventScroll, { passive: false });
      }
      
      return () => {
        if ('ontouchstart' in window) {
          window.removeEventListener('scroll', preventScroll);
        }
      };
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropdown();
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      toggleDropdown();
    } else if (e.key === 'ArrowDown' && isOpen) {
      e.preventDefault();
      const firstItem = dropdownRef.current.querySelector('.dropdown-item');
      if (firstItem) firstItem.focus();
    }
  };

  const handleItemKeyDown = (e, option, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOptionSelect(option, e);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextItem = e.currentTarget.nextElementSibling;
      if (nextItem) nextItem.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevItem = e.currentTarget.previousElementSibling;
      if (prevItem) {
        prevItem.focus();
      } else {
        dropdownRef.current.querySelector('.dropdown-header').focus();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      toggleDropdown();
      dropdownRef.current.querySelector('.dropdown-header').focus();
    }
  };

  // Render checkbox with proper state
  const renderCheckbox = (option, selectionState) => {
    return (
      <div style={{
        width: '22px',
        height: '22px',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.4)' : '1px solid #ccc',
        borderRadius: '3px',
        marginRight: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: selectionState === 'selected' 
          ? (isDarkMode ? '#0074cc' : '#1976d2') 
          : selectionState === 'partial'
          ? (isDarkMode ? '#4a90e2' : '#64b5f6')
          : 'transparent',
        flexShrink: 0
      }}>
        {selectionState === 'selected' && (
          <span style={{ color: 'white', fontSize: '13px' }}>✓</span>
        )}
        {selectionState === 'partial' && (
          <span style={{ 
            color: 'white', 
            fontSize: '16px', 
            fontWeight: 'bold',
            lineHeight: '1'
          }}>−</span>
        )}
      </div>
    );
  };

  // Recursive function to render options
  const renderOptions = (options, depth = 0) => {
    if (!Array.isArray(options)) {
      return null;
    }
    
    return options.flatMap((option, index) => {
      const optionText = typeof option === 'object' && option !== null ? 
        (option.label || option.name || option.toString()) : 
        option;
      const optionKey = typeof option === 'object' && option !== null ? 
        (option.id || option.name || option.toString()) : 
        option;
      const hasChildren = typeof option === 'object' && option !== null && 'children' in option && Array.isArray(option.children) && option.children.length > 0;
      const isExpanded = hasChildren && expandedItems[optionKey];
      const selectionState = getSelectionState(option);
      
      const result = [
        <div 
          key={`${optionKey}-${depth}-${index}`}
          className={`dropdown-item ${hasChildren ? 'parent-item' : 'child-item'}`}
          onClick={(e) => handleOptionSelect(option, e)}
          onKeyDown={(e) => handleItemKeyDown(e, option, index)}
          role="option"
          aria-selected={selectionState === 'selected'}
          aria-expanded={hasChildren ? isExpanded : undefined}
          tabIndex="0"
          style={{
            padding: '9.6px 12px',
            paddingLeft: `${15 + (depth * 20)}px`,
            cursor: 'pointer',
            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
            display: 'flex',
            alignItems: 'center',
            transition: 'background 0.2s',
            background: selectionState === 'selected' || selectionState === 'partial'
              ? (isDarkMode ? 'rgba(61, 139, 255, 0.18)' : 'rgba(25, 118, 210, 0.1)')
              : 'transparent',
            fontSize: '14px',
            fontWeight: hasChildren ? '600' : '400',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            color: hasChildren ? (isDarkMode ? '#e2e8f0' : '#2d3748') : themeStyles.text.primary,
            outline: 'none',
            minHeight: '17.6px',
            boxSizing: 'border-box',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            if (selectionState === 'unselected') {
              e.currentTarget.style.background = isDarkMode 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.03)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = selectionState === 'selected' || selectionState === 'partial'
              ? (isDarkMode ? 'rgba(61, 139, 255, 0.18)' : 'rgba(25, 118, 210, 0.1)')
              : 'transparent';
          }}
        >
          {/* Hierarchy lines for child items */}
          {depth > 0 && !hasChildren && (
            <>
              <div className="hierarchy-line" style={{
                position: 'absolute',
                left: `${7 + ((depth - 1) * 20)}px`,
                top: 0,
                height: '100%',
                width: '1px',
                background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
              }}></div>
              <div className="hierarchy-branch" style={{
                position: 'absolute',
                left: `${7 + ((depth - 1) * 20)}px`,
                top: '50%',
                width: '8px',
                height: '1px',
                background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
              }}></div>
            </>
          )}
          
          {/* Expand/collapse icon for parent items */}
          {hasChildren && (
            <span 
              className={`expansion-toggle ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExpandedItems(prev => ({
                  ...prev,
                  [optionKey]: !prev[optionKey]
                }));
              }}
              style={{ 
                cursor: 'pointer', 
                marginRight: '8px',
                display: 'inline-flex',
                width: '24px',
                height: '24px',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                borderRadius: '3px',
                transition: 'all 0.15s ease',
                zIndex: 5
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode 
                  ? 'rgba(255, 255, 255, 0.15)' 
                  : 'rgba(0, 0, 0, 0.08)';
                e.stopPropagation();
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isDarkMode 
                  ? 'rgba(255, 255, 255, 0.08)' 
                  : 'rgba(0, 0, 0, 0.04)';
                e.stopPropagation();
              }}
            >
              <FontAwesomeIcon 
                icon={isExpanded ? faChevronDown : faChevronRight} 
                style={{ 
                  fontSize: `${Math.round(12 * s)}px`,
                  color: isDarkMode ? '#a0aec0' : '#666',
                  pointerEvents: 'none',
                  transition: 'transform 0.2s ease'
                }}
              />
            </span>
          )}
          
          {/* Spacing for non-parent items */}
          {!hasChildren && (
            <span style={{ width: '24px', marginRight: '8px' }}></span>
          )}
          
          {/* Checkbox */}
          {renderCheckbox(option, selectionState)}
          
          {/* Option text */}
          {optionText}
        </div>
      ];
      
      // Render children if expanded
      if (hasChildren && isExpanded) {
        result.push(...renderOptions(option.children, depth + 1));
      }
      
      return result;
    });
  };

  return (
    <div className="custom-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Dropdown trigger button */}
      <div 
        className="dropdown-header"
        ref={headerRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex="0"
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        style={{
          background: themeStyles.select.background,
          color: themeStyles.select.color,
          border: themeStyles.select.border,
          borderRadius: `${baseBorderRadius}px`,
          padding: `${basePaddingY}px ${basePaddingX}px`,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease',
          outline: 'none',
          width: '200px'
        }}
      >
        <span style={{ marginRight: '5px' }}>
          {displayText}
        </span>
        <FontAwesomeIcon 
          icon={isOpen ? faArrowCircleUp : faArrowCircleDown} 
          style={{ fontSize: `${Math.round(14 * s)}px` }}
        />
      </div>
      
      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="dropdown-menu"
          ref={menuRef}
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: (isInSimulateExam || isInPracticeTest) ? 'absolute' : 'fixed',
            ...(dropdownPosition === 'bottom' 
              ? { top: headerRef.current ? headerRef.current.getBoundingClientRect().bottom + 5 : 0 }
              : { bottom: window.innerHeight - (headerRef.current ? headerRef.current.getBoundingClientRect().top : 0) + 5 }),
            left: headerRef.current ? headerRef.current.getBoundingClientRect().left : 0,
            zIndex: 9999,
            minWidth: headerRef.current ? headerRef.current.offsetWidth : '100%',
            width: dropdownWidth === 'auto' ? 'auto' : `${dropdownWidth}px`,
            maxWidth: `min(${dropdownWidth === 'auto' ? 300 : dropdownWidth}px, calc(100vw - 40px))`,
            background: isDarkMode ? '#1c2740' : '#fff',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6',
            borderRadius: '5px',
            maxHeight: `${baseItemHeight * 5}px`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
          }}
        >
          {/* Search input */}
          <div style={{
            padding: `${Math.round(12 * s)}px`,
            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            background: isDarkMode ? '#1c2740' : '#fff',
            flexShrink: 0
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <FontAwesomeIcon 
                icon={faSearch} 
                style={{
                  position: 'absolute',
                  left: `${Math.round(10 * s)}px`,
                  color: isDarkMode ? '#a0aec0' : '#666',
                  fontSize: `${Math.round(14 * s)}px`,
                  pointerEvents: 'none'
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: `${Math.round(8 * s)}px ${Math.round(12 * s)}px ${Math.round(8 * s)}px ${Math.round(35 * s)}px`,
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6',
                  borderRadius: '4px',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                  color: isDarkMode ? '#e2e8f0' : '#2d3748',
                  fontSize: `${Math.round(14 * s)}px`,
                  outline: 'none'
                }}
                onKeyDown={(e) => {
                  // Prevent dropdown from closing when typing
                  e.stopPropagation();
                  
                  // Handle escape key to clear search
                  if (e.key === 'Escape') {
                    if (searchTerm) {
                      setSearchTerm('');
                    } else {
                      toggleDropdown();
                    }
                  }
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: `${Math.round(8 * s)}px`,
                    background: 'none',
                    border: 'none',
                    color: isDarkMode ? '#a0aec0' : '#666',
                    cursor: 'pointer',
                    padding: `${Math.round(2 * s)}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} style={{ fontSize: `${Math.round(12 * s)}px` }} />
                </button>
              )}
            </div>
          </div>

          {/* Options list with scroll container */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0
          }}>
            {filteredOptions.length > 0 ? (
              renderOptions(filteredOptions)
            ) : searchTerm ? (
              <div style={{
                padding: `${Math.round(16 * s)}px`,
                textAlign: 'center',
                color: isDarkMode ? '#a0aec0' : '#666',
                fontSize: `${Math.round(14 * s)}px`
              }}>
                No items found matching "{searchTerm}"
              </div>
            ) : (
              renderOptions(filteredOptions)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchicalMultiSelectDropdown;
